import {ICrudDataSource} from './data-source';
import {ErrorResponse} from './error-response.record';
import {fromJson, json, toJson} from './json.mapper';
import {map} from 'lodash';
import {ListResponseRecord} from './list-response.record';
import {withCsrfHeaders} from '../services/csrf';

export interface HttpCrudV2Response<T> {
    success?: boolean;
    error?: ErrorResponse;
    result?: T | T[];
    meta?: ListResponseRecord;
}

// example, need to change request and response params
export abstract class HttpCrudV2DataSource<T> implements ICrudDataSource<T> {
    protected abstract apiPath: string;

    constructor(protected recordClass: {new (): T}) {}

    async update(record: T, id: string | number): Promise<HttpCrudV2Response<T>> {
        const response = await fetch(`${this.apiPath}/${id}`, {
            method: 'PUT',
            headers: withCsrfHeaders('PUT', {
                'Content-Type': 'application/json'
            }),
            body: JSON.stringify(toJson(record)),
            credentials: 'include'
        });
        switch (response.status) {
            case 200:
                return {
                    success: true,
                    result: fromJson(this.recordClass, await response.json())
                };
            case 204:
                return {success: true};
            case 400:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 403:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 404:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 409:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
        }
    }

    async get(id: number | string): Promise<HttpCrudV2Response<T>> {
        const response = await fetch(`${this.apiPath}/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });
        switch (response.status) {
            case 200:
                return {success: true, result: fromJson(this.recordClass, await response.json())};
            case 404:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 403:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
        }
    }

    async remove(id: number | string): Promise<HttpCrudV2Response<T>> {
        const response = await fetch(`${this.apiPath}/${id}`, {
            method: 'DELETE',
            headers: withCsrfHeaders('DELETE', {
                'Content-Type': 'application/json'
            }),
            credentials: 'include'
        });
        switch (response.status) {
            case 204:
            case 200:
                return {success: true};
            case 404:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 403:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 409:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
        }
    }

    async list(parameters): Promise<HttpCrudV2Response<T>> {
        /**TODO filter, sort, etc */
        const params = parameters;
        const queryString = map(params, (v, k) => `${k}=${v}`).join('&');
        const response = await fetch(queryString ? this.apiPath + `?${queryString}` : '', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });
        switch (response.status) {
            case 200:
                const result = await response.json();
                return {
                    success: true,
                    result: result.elements.map((list: any) => fromJson(this.recordClass, list)),
                    meta: fromJson(ListResponseRecord, result)
                };
            case 404:
                return {success: false, error: fromJson(ErrorResponse, await response.json())};
            case 403:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 400:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
        }
    }

    async create(record: T): Promise<HttpCrudV2Response<T>> {
        const response = await fetch(this.apiPath, {
            method: 'POST',
            headers: withCsrfHeaders('POST', {
                'Content-Type': 'application/json'
            }),
            body: JSON.stringify(toJson(record)),
            credentials: 'include'
        });
        switch (response.status) {
            case 200:
                return {
                    success: true,
                    result: fromJson(this.recordClass, await response.json())
                };
            case 204:
                return {success: true};
            case 400:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 403:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 409:
                return {
                    success: false,
                    error: fromJson(ErrorResponse, await response.json())
                };
            case 404:
                return {success: false, error: fromJson(ErrorResponse, await response.json())};
        }
    }
}
